import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { generateRetentionActions, DEDUP_WINDOW_DAYS, RetentionAction } from '@/lib/churn-actions';
import { ChurnRiskScore } from '@/lib/churn-risk';

export interface RetentionActionRow {
  id: string;
  client_id: string;
  client_name?: string;
  triggered_by: string;
  risk_level: string;
  action_text: string;
  action_type: string;
  priority: string;
  status: 'pending' | 'snoozed' | 'completed' | 'dismissed';
  due_date: string | null;
  completed_at: string | null;
  snoozed_until: string | null;
  dismiss_reason: string | null;
  outcome_notes: string | null;
  churn_score_at_creation: number | null;
  created_at: string;
}

/**
 * Hook for managing retention actions for a single client.
 * Call syncActions() after churn risk is recalculated to generate new actions.
 */
export function useRetentionActions(clientId?: string) {
  const [actions, setActions] = useState<RetentionActionRow[]>([]);
  const [loading, setLoading] = useState(true);

  const loadActions = useCallback(async () => {
    if (!clientId) {
      setActions([]);
      setLoading(false);
      return;
    }

    try {
      const { data } = await supabase
        .from('retention_actions')
        .select('*')
        .eq('client_id', clientId)
        .in('status', ['pending', 'snoozed'])
        .order('created_at', { ascending: false });

      setActions((data as RetentionActionRow[]) || []);
    } catch (error) {
      console.error('Error loading retention actions:', error);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadActions();
  }, [loadActions]);

  /**
   * Generate new retention actions based on current churn risk,
   * with deduplication against recent actions.
   */
  const syncActions = useCallback(async (
    clientName: string,
    riskScore: ChurnRiskScore,
    organizationId?: string | null,
  ) => {
    if (!clientId) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // If risk is low, auto-resolve all pending actions for this client
      if (riskScore.level === 'low') {
        await supabase
          .from('retention_actions')
          .update({ status: 'completed', completed_at: new Date().toISOString(), outcome_notes: 'Auto-resolved: client risk level improved to low' })
          .eq('client_id', clientId)
          .eq('status', 'pending');
        await loadActions();
        return;
      }

      const newActions = generateRetentionActions(clientName, riskScore);
      if (newActions.length === 0) return;

      // Check for recent actions to deduplicate
      const dedupCutoff = new Date();
      dedupCutoff.setDate(dedupCutoff.getDate() - DEDUP_WINDOW_DAYS);

      const { data: recentActions } = await supabase
        .from('retention_actions')
        .select('triggered_by, created_at')
        .eq('client_id', clientId)
        .gte('created_at', dedupCutoff.toISOString());

      const recentFactors = new Set(
        (recentActions || []).map(a => a.triggered_by)
      );

      // Filter out actions that already exist within the dedup window
      const actionsToCreate = newActions.filter(
        a => !recentFactors.has(a.triggered_by)
      );

      if (actionsToCreate.length === 0) return;

      // Insert new actions
      const rows = actionsToCreate.map(a => ({
        user_id: user.id,
        organization_id: organizationId || null,
        client_id: clientId,
        triggered_by: a.triggered_by,
        risk_level: a.risk_level,
        action_text: a.action_text,
        action_type: a.action_type,
        priority: a.priority,
        status: 'pending',
        churn_score_at_creation: riskScore.score,
      }));

      await supabase.from('retention_actions').insert(rows);
      await loadActions();
    } catch (error) {
      console.error('Error syncing retention actions:', error);
    }
  }, [clientId, loadActions]);

  const completeAction = useCallback(async (actionId: string, notes?: string) => {
    await supabase
      .from('retention_actions')
      .update({
        status: 'completed',
        completed_at: new Date().toISOString(),
        outcome_notes: notes || null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', actionId);
    await loadActions();
  }, [loadActions]);

  const snoozeAction = useCallback(async (actionId: string, days: number) => {
    const snoozeUntil = new Date();
    snoozeUntil.setDate(snoozeUntil.getDate() + days);
    await supabase
      .from('retention_actions')
      .update({
        status: 'snoozed',
        snoozed_until: snoozeUntil.toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', actionId);
    await loadActions();
  }, [loadActions]);

  const dismissAction = useCallback(async (actionId: string, reason?: string) => {
    await supabase
      .from('retention_actions')
      .update({
        status: 'dismissed',
        dismiss_reason: reason || null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', actionId);
    await loadActions();
  }, [loadActions]);

  return {
    actions,
    loading,
    syncActions,
    completeAction,
    snoozeAction,
    dismissAction,
    reload: loadActions,
  };
}

/**
 * Hook for the dashboard — fetches all pending retention actions across all clients.
 */
export function useAllRetentionActions() {
  const [actions, setActions] = useState<RetentionActionRow[]>([]);
  const [loading, setLoading] = useState(true);

  const loadActions = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Fetch pending + snoozed (where snooze has expired) actions
      const now = new Date().toISOString();
      const { data: pendingActions } = await supabase
        .from('retention_actions')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
        .limit(50);

      const { data: expiredSnoozed } = await supabase
        .from('retention_actions')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'snoozed')
        .lte('snoozed_until', now)
        .order('created_at', { ascending: false })
        .limit(20);

      // Un-snooze expired actions
      if (expiredSnoozed && expiredSnoozed.length > 0) {
        const expiredIds = expiredSnoozed.map(a => a.id);
        await supabase
          .from('retention_actions')
          .update({ status: 'pending', snoozed_until: null, updated_at: now })
          .in('id', expiredIds);
      }

      const allActions = [
        ...((pendingActions as RetentionActionRow[]) || []),
        ...((expiredSnoozed as RetentionActionRow[]) || []).map(a => ({ ...a, status: 'pending' as const })),
      ];

      // Enrich with client names
      const clientIds = [...new Set(allActions.map(a => a.client_id))];
      if (clientIds.length > 0) {
        const { data: clients } = await supabase
          .from('clients')
          .select('id, name')
          .in('id', clientIds);

        const clientMap = new Map((clients || []).map(c => [c.id, c.name]));
        for (const action of allActions) {
          action.client_name = clientMap.get(action.client_id);
        }
      }

      // Sort by priority
      const priorityOrder: Record<string, number> = { urgent: 0, high: 1, medium: 2, low: 3 };
      allActions.sort((a, b) => (priorityOrder[a.priority] ?? 3) - (priorityOrder[b.priority] ?? 3));

      setActions(allActions);
    } catch (error) {
      console.error('Error loading all retention actions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadActions();
  }, [loadActions]);

  const completeAction = useCallback(async (actionId: string, notes?: string) => {
    await supabase
      .from('retention_actions')
      .update({
        status: 'completed',
        completed_at: new Date().toISOString(),
        outcome_notes: notes || null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', actionId);
    await loadActions();
  }, [loadActions]);

  const snoozeAction = useCallback(async (actionId: string, days: number) => {
    const snoozeUntil = new Date();
    snoozeUntil.setDate(snoozeUntil.getDate() + days);
    await supabase
      .from('retention_actions')
      .update({
        status: 'snoozed',
        snoozed_until: snoozeUntil.toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', actionId);
    await loadActions();
  }, [loadActions]);

  const dismissAction = useCallback(async (actionId: string, reason?: string) => {
    await supabase
      .from('retention_actions')
      .update({
        status: 'dismissed',
        dismiss_reason: reason || null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', actionId);
    await loadActions();
  }, [loadActions]);

  return {
    actions,
    loading,
    completeAction,
    snoozeAction,
    dismissAction,
    reload: loadActions,
    pendingCount: actions.filter(a => a.status === 'pending').length,
    urgentCount: actions.filter(a => a.priority === 'urgent').length,
  };
}
